import React from "react";
import { Link } from "react-router-dom";
import {
  Search, ShieldCheck, ArrowRight, Star, MapPin, Sparkles, Clock4, Check,
  Lightbulb, Palette, Award, FileText, Globe2, Boxes, Crown, ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScheduleNudge from "../components/ScheduleNudge";
import {
  HERO_IMAGES, STATS, IP_CATEGORIES, HOW_IT_WORKS, TESTIMONIALS,
  PARTNERS, PRICING_TIERS, PROVIDERS,
} from "../lib/mockData";

const ICONS = { Lightbulb, Palette, Award, FileText, Globe2, Boxes };

export default function Home() {
  return (
    <div className="surface-cream min-h-screen" data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] blob bg-[#7CC242]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] blob bg-[#0E6E5E]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-16 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="fade-up">
            <div className="chip mb-6">
              <ShieldCheck size={13}/> India's most trusted IP marketplace
            </div>
            <h1 className="font-display-tight text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#0A2520] leading-[1.02] mb-6">
              File your patent <br/>
              with the right <span className="text-[#0E6E5E]">attorney</span>, <br/>
              at the right <span className="relative inline-block">
                <span className="relative z-10">price.</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-[#7CC242]/50 -z-0" />
              </span>
            </h1>
            <p className="text-lg text-[#3D4F4A] leading-relaxed max-w-xl mb-8">
              Describe your innovation in 3 minutes. Compare quotes from 850+ certified patent attorneys.
              Schedule, file, and track to grant — all in one place.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <Link to="/submit" className="btn-primary" data-testid="hero-cta-submit">
                Submit your innovation <ArrowRight size={16}/>
              </Link>
              <button className="btn-accent" data-testid="hero-cta-schedule">
                Schedule a Free Call
              </button>
            </div>
            <div className="flex items-center gap-5 text-sm text-[#5A6B66]">
              <div className="flex -space-x-2">
                {[HERO_IMAGES.scientistA, HERO_IMAGES.lawyerA, HERO_IMAGES.lawyerB, HERO_IMAGES.scientistB].map((src, i) => (
                  <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover" />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-[#0A2520] font-semibold">
                  <Star size={14} className="fill-[#7CC242] text-[#7CC242]" /> 4.9 from 2,400+ innovators
                </div>
                <div>Trusted by IIT, IIM, DPIIT-recognised startups</div>
              </div>
            </div>
          </div>

          {/* Hero visual bento */}
          <div className="relative grid grid-cols-2 gap-4 fade-up-1">
            <div className="rounded-2xl overflow-hidden h-56 col-span-2 relative">
              <img src={HERO_IMAGES.innovators} alt="Indian innovators" className="w-full h-full object-cover" />
              <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-semibold text-[#0A2520]">
                <span className="inline-block w-2 h-2 rounded-full bg-[#7CC242] mr-1.5 animate-pulse"></span>
                850 attorneys online now
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-44">
              <img src={HERO_IMAGES.scientistA} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="card p-5 flex flex-col justify-between h-44">
              <div className="chip-accent chip">Live quote</div>
              <div>
                <div className="font-display font-extrabold text-3xl text-[#0A2520]">₹14,900</div>
                <div className="text-xs text-[#5A6B66]">utility patent · 7-10 days</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-[#E1E8E2] bg-white py-6">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 flex items-center justify-between gap-8 flex-wrap">
          <div className="text-xs uppercase tracking-widest text-[#5A6B66] font-semibold">Trusted by innovators at</div>
          <div className="flex items-center gap-8 lg:gap-12 flex-wrap">
            {PARTNERS.map((p) => (
              <span key={p} className="text-sm font-semibold text-[#3D4F4A] opacity-70 hover:opacity-100 transition">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="chip mb-3">What can we protect?</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520]">Every kind of IP, one marketplace.</h2>
          </div>
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6B66]" />
            <input placeholder="Try ‘software patent’ or ‘design protection’" className="input pl-11 rounded-full" data-testid="home-search" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {IP_CATEGORIES.map((c) => {
            const Icon = ICONS[c.icon] || Lightbulb;
            return (
              <div key={c.name} className="card p-6 cursor-pointer group" data-testid={`category-${c.name}`}>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-[#EBF3EC] flex items-center justify-center text-[#0E6E5E] flex-shrink-0">
                    <Icon size={22} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#0A2520] text-lg">{c.name}</div>
                    <div className="text-xs text-[#5A6B66] mt-0.5">{c.count.toLocaleString()} filings managed</div>
                  </div>
                </div>
                <p className="text-sm text-[#3D4F4A] mb-4 leading-relaxed">{c.desc}</p>
                <div className="flex items-center justify-between pt-3 border-t border-[#F0F4F0]">
                  <span className="text-xs text-[#0E6E5E] font-semibold uppercase tracking-wider">Get a quote</span>
                  <ArrowRight size={14} className="text-[#0E6E5E] group-hover:translate-x-1 transition" />
                </div>
              </div>
            );
          })}
        </div>

        {/* NUDGE #1 — inline after categories */}
        <div className="mt-12">
          <ScheduleNudge variant="compact" message="Confused about which protection you need?" />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-20 border-y border-[#E1E8E2] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="chip mb-3">How it works</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-3">From idea to grant — four simple steps.</h2>
            <p className="text-[#5A6B66]">Built for first-time innovators and seasoned IP managers alike.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="card p-6">
                <div className="text-xs font-mono text-[#7CC242] font-bold mb-3">{s.step}</div>
                <div className="font-display font-bold text-xl text-[#0A2520] mb-2">{s.title}</div>
                <p className="text-sm text-[#5A6B66] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NUDGE #2 — dark banner */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
        <ScheduleNudge variant="dark" />
      </section>

      {/* FEATURED PREMIUM ATTORNEYS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="chip-gold chip mb-3"><Crown size={12}/> Premium Profiles</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520]">Top-rated patent firms this month</h2>
          </div>
          <div className="flex gap-2">
            <Link to="/attorneys/premium" className="btn-outline text-sm" data-testid="home-view-premium">View Premium →</Link>
            <Link to="/attorneys" className="btn-primary text-sm" data-testid="home-view-all">Browse all 850+</Link>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROVIDERS.filter(p => p.premium).slice(0, 3).map((p) => (
            <Link key={p.id} to={`/attorneys/${p.id}`} className="card p-5 block relative" data-testid={`home-provider-${p.id}`}>
              <div className="absolute top-4 right-4 chip chip-gold"><Crown size={10}/> Premium</div>
              <div className="flex items-center gap-3 mb-4">
                <img src={p.avatar} alt={p.name} className="w-14 h-14 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#0A2520] truncate">{p.name}</div>
                  <div className="text-xs text-[#5A6B66] flex items-center gap-1"><MapPin size={11}/> {p.location}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-4 text-sm">
                <span className="flex items-center gap-1 font-semibold"><Star size={14} className="fill-[#7CC242] text-[#7CC242]" />{p.rating}</span>
                <span className="text-[#5A6B66]">·</span>
                <span className="text-[#5A6B66]">{p.patentsFiled} patents</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {p.specializations.slice(0, 3).map((s) => <span key={s} className="chip">{s}</span>)}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[#F0F4F0]">
                <div>
                  <div className="text-xs text-[#5A6B66]">From</div>
                  <div className="font-display font-bold text-lg text-[#0A2520]">₹{p.priceFrom.toLocaleString('en-IN')}</div>
                </div>
                <div className="text-xs text-[#5A6B66] text-right">
                  <div className="flex items-center gap-1"><Clock4 size={11} /> {p.turnaround}</div>
                  <div>Responds {p.response}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="surface-sage py-20 border-y border-[#D5E2D6] mt-12">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display font-extrabold text-4xl sm:text-5xl text-[#0E6E5E] mb-2">{s.value}</div>
              <div className="text-sm text-[#3D4F4A]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CONCIERGE TEASER */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="p-8 lg:p-12 grid lg:grid-cols-[1.3fr_1fr] gap-10 bg-[#0A2520] text-white rounded-3xl border border-[#0A2520]" data-testid="home-concierge-teaser">
          <div>
            <div className="chip-gold chip mb-4"><Sparkles size={12}/> Premium Concierge</div>
            <h3 className="font-display font-extrabold text-3xl sm:text-4xl mb-4 leading-tight">
              Don't have time to compare quotes? <br/>
              <span className="text-[#7CC242]">Let us handle everything.</span>
            </h3>
            <p className="text-white/70 mb-7 max-w-xl leading-relaxed">
              Our concierge team takes the entire patent journey off your plate — from eligibility & drafting through filing,
              office-action responses, and grant. Dedicated IP manager. Single fixed price.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-7">
              {["Dedicated IP manager","End-to-end filing","Cross-border coverage","Quarterly strategy reviews"].map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm">
                  <Check size={14} className="text-[#7CC242]"/> {f}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/concierge" className="btn-accent text-sm">Explore Concierge <ArrowRight size={14}/></Link>
              <button className="btn-outline text-sm bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A2520]">Schedule Discovery Call</button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-72 lg:h-auto relative">
            <img src={HERO_IMAGES.handshake} alt="" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#5A6B66] font-semibold mb-1">Concierge clients save avg.</div>
              <div className="font-display font-extrabold text-3xl text-[#0E6E5E]">11 weeks</div>
              <div className="text-xs text-[#5A6B66]">vs. self-managed filings</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOR ATTORNEYS ONBOARDING SECTION */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16" data-testid="home-for-attorneys">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center card p-8 lg:p-12 bg-gradient-to-br from-[#EBF3EC] via-white to-[#F1F8E2] border-[#7CC242]/40">
          <div>
            <div className="chip chip-gold mb-4 inline-flex"><Crown size={13}/> For Patent Attorneys &amp; Law Firms</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A2520] mb-4 leading-tight">
              Grow your IP practice on <br/>
              <span className="text-[#0E6E5E]">India's largest patent marketplace.</span>
            </h2>
            <p className="text-lg text-[#3D4F4A] leading-relaxed mb-6">
              Reach 12,000+ innovators, startups, and corporates. Zero acquisition cost — pay only when you accept a matched brief.
              Free to join. Upgrade to Premium anytime.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-7">
              {[
                "4× more qualified leads",
                "Verified badge & trust",
                "PAN-India visibility",
                "Onboard in < 5 minutes",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2.5 text-[15px] text-[#0A2520] font-semibold">
                  <div className="w-6 h-6 rounded-full bg-[#0E6E5E] flex items-center justify-center flex-shrink-0">
                    <Check size={13} className="text-white"/>
                  </div>
                  {f}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/attorneys/join" className="btn-primary" data-testid="home-attorney-join-cta">
                Start Onboarding <ArrowRight size={15}/>
              </Link>
              <Link to="/attorneys/premium" className="btn-outline">See Premium benefits</Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="card p-6 bg-white">
              <div className="font-display font-extrabold text-4xl text-[#0E6E5E] mb-1">850+</div>
              <div className="text-sm text-[#5A6B66]">Verified firms already on Udhyak</div>
            </div>
            <div className="card p-6 bg-white">
              <div className="font-display font-extrabold text-4xl text-[#0E6E5E] mb-1">₹18K</div>
              <div className="text-sm text-[#5A6B66]">Average lead value per brief</div>
            </div>
            <div className="card p-6 bg-white">
              <div className="font-display font-extrabold text-4xl text-[#0E6E5E] mb-1">&lt; 24h</div>
              <div className="text-sm text-[#5A6B66]">To go live after credential check</div>
            </div>
            <div className="card p-6 bg-white">
              <div className="font-display font-extrabold text-4xl text-[#0E6E5E] mb-1">4×</div>
              <div className="text-sm text-[#5A6B66]">More leads vs standard listings</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="chip mb-3">Stories from innovators</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-10 max-w-2xl">
          12,000+ patents filed. Real innovators, real grants.
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="card p-6">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[#7CC242] text-[#7CC242]" />)}
              </div>
              <p className="text-[#3D4F4A] mb-5 leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-[#F0F4F0]">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="font-display font-semibold text-sm text-[#0A2520]">{t.name}</div>
                  <div className="text-xs text-[#5A6B66]">{t.role} · {t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-white py-20 border-y border-[#E1E8E2] mt-12">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="chip inline-flex mb-3">Pricing</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-3">One platform. Three plans.</h2>
            <p className="text-[#5A6B66]">Innovators always pay attorneys directly. Pick a plan based on how often you file.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {PRICING_TIERS.map((t) => (
              <div key={t.name} className={`card p-7 relative ${t.highlight ? "ring-2 ring-[#0E6E5E] shadow-lg" : ""}`} data-testid={`tier-${t.name}`}>
                {t.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip chip-dark">Most popular</div>}
                <div className="font-display font-bold text-xl text-[#0A2520] mb-1">{t.name}</div>
                <div className="text-sm text-[#5A6B66] mb-5">{t.desc}</div>
                <div className="mb-6">
                  <span className="font-display font-extrabold text-5xl text-[#0A2520]">
                    {typeof t.price === "number" ? (t.price === 0 ? "₹0" : `₹${t.price.toLocaleString('en-IN')}`) : t.price}
                  </span>
                  <span className="text-sm text-[#5A6B66] ml-2">{t.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {t.features.slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#3D4F4A]">
                      <Check size={16} className="text-[#0E6E5E] flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button className={t.highlight ? "btn-primary w-full justify-center" : "btn-outline w-full justify-center"}>{t.cta}</button>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing" className="text-sm font-semibold text-[#0E6E5E] hover:underline inline-flex items-center gap-1">
              See full pricing comparison <ChevronRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA + NUDGE */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <ScheduleNudge message="Your idea deserves protection. Let's talk." />
        <div className="text-center mt-12">
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-[#0A2520] mb-5 max-w-3xl mx-auto leading-tight">
            Your idea deserves protection. <br/>
            <span className="text-[#0E6E5E]">Start in under 3 minutes.</span>
          </h2>
          <p className="text-[#5A6B66] mb-8 max-w-xl mx-auto">No credit card. No commitment. Get matched with verified attorneys instantly.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/submit" className="btn-primary" data-testid="home-final-cta">
              Submit your innovation <ArrowRight size={16}/>
            </Link>
            <button className="btn-outline">Schedule a Free Call</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
